import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/hooks/shared';
import { ROUTES } from '@/config/routes';

interface AuthLayoutProps {
  children: ReactNode;
  showSignUpLink?: boolean;
}

/**
 * Auth layout wrapper
 * Provides consistent header and footer for auth pages
 */
export function AuthLayout({ children, showSignUpLink = true }: AuthLayoutProps) {
  const { t } = useTranslation('auth');
  const { changeLanguage, currentLanguage } = useLanguage();

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background dark:bg-[#121520]">
      <div className="layout-container flex h-full grow flex-col">
        {/* Header - Responsive: Mobile (compact) → Tablet → Desktop */}
        <header className="flex items-center justify-between whitespace-nowrap border-b border-solid border-primary/10 px-3 sm:px-4 md:px-6 lg:px-20 py-3 md:py-4 bg-background/80 dark:bg-[#121520]/80 backdrop-blur-md sticky top-0 z-50">
          {/* Logo - Responsive sizing */}
          <Link to={ROUTES.HOME} className="flex items-center gap-2 md:gap-2.5 lg:gap-3 text-primary flex-shrink-0">
            <div className="size-7 md:size-8 lg:size-9 flex items-center justify-center bg-primary/10 rounded-md lg:rounded-lg">
              <span className="material-symbols-outlined text-primary text-xl md:text-2xl">account_tree</span>
            </div>
            <h2 className="text-slate-900 dark:text-slate-100 text-sm sm:text-base md:text-lg lg:text-xl font-bold leading-tight tracking-[-0.015em]">
              <span className="hidden sm:inline">FamilyTreeBuilder</span>
              <span className="sm:hidden">FTB</span>
            </h2>
          </Link>

          {/* Language Switcher & Sign Up - Responsive spacing and sizing */}
          <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 flex-shrink-0">
            {/* Language Switcher - Compact on mobile */}
            <div className="flex items-center gap-1 sm:gap-1.5 lg:gap-2 border border-primary/20 rounded-full p-0.5 lg:p-1">
              <button
                onClick={() => changeLanguage('en')}
                className={`px-2 sm:px-2.5 lg:px-3 py-0.5 lg:py-1 text-[10px] sm:text-xs font-semibold rounded-full transition-colors ${
                  currentLanguage === 'en'
                    ? 'bg-primary text-white'
                    : 'text-slate-600 dark:text-slate-400 hover:text-primary'
                }`}
                aria-label="Switch to English"
                title="English"
              >
                EN
              </button>
              <button
                onClick={() => changeLanguage('ar')}
                className={`px-2 sm:px-2.5 lg:px-3 py-0.5 lg:py-1 text-[10px] sm:text-xs font-semibold rounded-full transition-colors ${
                  currentLanguage === 'ar'
                    ? 'bg-primary text-white'
                    : 'text-slate-600 dark:text-slate-400 hover:text-primary'
                }`}
                aria-label="Switch to Arabic"
                title="العربية"
              >
                ع
              </button>
            </div>

            {/* Sign Up Link - Responsive sizing and text */}
            {showSignUpLink && (
              <>
                <span className="hidden lg:inline text-xs lg:text-sm text-slate-500 dark:text-slate-400">
                  {t('login.newHere', 'New here?')}
                </span>
                <Link
                  to={ROUTES.REGISTER}
                  className="flex min-w-[80px] sm:min-w-[90px] md:min-w-[100px] cursor-pointer items-center justify-center overflow-hidden rounded-full h-8 sm:h-9 md:h-10 px-3 sm:px-4 md:px-5 bg-primary/10 text-primary text-xs sm:text-sm font-bold hover:bg-primary/20 transition-colors"
                >
                  <span className="hidden sm:inline">{t('login.signUp', 'Sign Up')}</span>
                  <span className="sm:hidden">Join</span>
                </Link>
              </>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center p-6">
          {children}
        </main>

        {/* Footer - Responsive layout and spacing */}
        <footer className="px-3 sm:px-4 md:px-6 lg:px-20 py-4 sm:py-6 md:py-8 border-t border-primary/5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
            <div className="flex flex-wrap justify-center md:justify-start gap-3 sm:gap-4 md:gap-6 text-[10px] sm:text-xs text-slate-400 dark:text-slate-500">
              <a href="#" className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors whitespace-nowrap">
                {t('footer.privacy', 'Privacy Policy')}
              </a>
              <a href="#" className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors whitespace-nowrap">
                {t('footer.terms', 'Terms of Service')}
              </a>
            </div>
            <p className="text-[9px] sm:text-[10px] text-slate-400 dark:text-slate-500 text-center md:text-right">
              {t('footer.copyright', '© 2026 FamilyTreeBuilder. Preserving history together.')}
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
